import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill } from "remotion";
import { loadFont } from "@remotion/google-fonts/Inter";
import { loadFont as loadDevanagari } from "@remotion/google-fonts/NotoSansDevanagari";
import type { QuoteCardData } from "./types";

const { fontFamily } = loadFont("normal", { weights: ["300", "400", "600", "700"], subsets: ["latin"] });
const { fontFamily: devFont } = loadDevanagari("normal", { weights: ["400", "700"], subsets: ["devanagari"] });

const GOLD = "#D4A853";
const BG = "rgba(0,0,0,0.88)";
const BORDER_GOLD = "rgba(212,168,83,0.35)";

export const QuoteCard: React.FC<{ data: QuoteCardData; durationInFrames: number }> = ({ data, durationInFrames }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const fadeIn = interpolate(frame, [0, Math.round(0.5 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
  });
  const fadeOut = interpolate(frame, [durationInFrames - Math.round(0.6 * fps), durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const scale = interpolate(frame, [0, Math.round(0.5 * fps)], [0.93, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });

  const isDevanagari = /[ऀ-ॿ]/.test(data.text);

  return (
    <AbsoluteFill
      style={{
        opacity: fadeIn * fadeOut,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          transform: `scale(${scale})`,
          background: BG,
          border: `1px solid ${BORDER_GOLD}`,
          borderRadius: 12,
          padding: "22px 32px 20px",
          backdropFilter: "blur(16px)",
          boxShadow: "0 12px 56px rgba(0,0,0,0.8)",
          width: 660,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative quote mark */}
        <div
          style={{
            color: "rgba(212,168,83,0.1)",
            fontSize: 120,
            fontFamily,
            position: "absolute",
            top: -28,
            left: 16,
            lineHeight: 1,
            fontWeight: 700,
            userSelect: "none",
          }}
        >
          &#8220;
        </div>

        {/* Quote text */}
        <div
          style={{
            color: "rgba(255,255,255,0.92)",
            fontSize: isDevanagari ? 17 : 15,
            fontWeight: 400,
            lineHeight: 1.65,
            fontFamily: isDevanagari ? devFont : fontFamily,
            position: "relative",
            zIndex: 1,
            marginTop: 10,
          }}
        >
          {data.text}
        </div>

        {data.translation && (
          <div
            style={{
              color: "rgba(255,255,255,0.5)",
              fontSize: 13,
              fontWeight: 400,
              fontFamily,
              marginTop: 12,
              lineHeight: 1.5,
              fontStyle: "italic",
            }}
          >
            {data.translation}
          </div>
        )}

        <div
          style={{
            marginTop: 16,
            paddingTop: 12,
            borderTop: `1px solid rgba(212,168,83,0.2)`,
            color: GOLD,
            fontSize: 11,
            fontWeight: 600,
            fontFamily,
            letterSpacing: "0.5px",
          }}
        >
          — {data.source}
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
